const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

import type {
  RazorpayCheckoutPrefill,
  RazorpayCheckoutSuccess,
} from '../models/subscription-payment.model';

interface RazorpayCheckoutOptions {
  key: string;
  order_id: string;
  currency: string;
  name: string;
  description: string;
  prefill?: RazorpayCheckoutPrefill;
  theme?: { color?: string };
  handler: (response: RazorpayCheckoutSuccess) => void;
  modal?: {
    ondismiss?: () => void;
  };
}

interface RazorpayCheckoutInstance {
  open(): void;
  on(event: 'payment.failed', handler: (response: unknown) => void): void;
}

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayCheckoutOptions) => RazorpayCheckoutInstance;
  }
}

let razorpayScriptPromise: Promise<void> | null = null;

function loadRazorpayScript(): Promise<void> {
  if (typeof window.Razorpay === 'function') {
    return Promise.resolve();
  }
  if (razorpayScriptPromise) {
    return razorpayScriptPromise;
  }

  razorpayScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${RAZORPAY_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Razorpay checkout script')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load Razorpay checkout script'));
    document.body.appendChild(script);
  });

  return razorpayScriptPromise;
}

export type RazorpayCheckoutInitiate = {
  razorpayKeyId: string;
  razorpayOrderId: string;
  currency: string;
};

export type OpenRazorpayCheckoutInput = {
  initiate: RazorpayCheckoutInitiate;
  merchantName: string;
  description: string;
  prefill?: RazorpayCheckoutPrefill;
};

export async function openRazorpayCheckout(
  input: OpenRazorpayCheckoutInput,
): Promise<RazorpayCheckoutSuccess> {
  await loadRazorpayScript();
  if (typeof window.Razorpay !== 'function') {
    throw new Error('Razorpay checkout is unavailable');
  }

  return new Promise<RazorpayCheckoutSuccess>((resolve, reject) => {
    const checkout = new window.Razorpay!({
      key: input.initiate.razorpayKeyId,
      order_id: input.initiate.razorpayOrderId,
      currency: input.initiate.currency || 'INR',
      name: input.merchantName,
      description: input.description,
      prefill: input.prefill,
      theme: { color: '#6729ff' },
      handler: (response) => resolve(response),
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled')),
      },
    });

    checkout.on('payment.failed', () => {
      reject(new Error('Payment failed. Please try again.'));
    });

    checkout.open();
  });
}
